"use client";

import { getDocument } from "@faire/web/common/globals/getDocument";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { ResponsiveProp } from "@faire/web/slate/Theme/ResponsiveProp";
import { useResponsiveProp } from "@faire/web/ui/hooks/useResponsiveProp";
import isChromatic from "chromatic/isChromatic";
import React, {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

interface IProps {
  children: React.ReactNode;

  /**
   * The placeholder given here will be used when this component is not close to visible
   * yet. If no placeholder is given, then the default will simply render a 10px high div.
   * On pages that have a lot of vertical scrolling, you should aim to have something that
   * is nearly as tall as the real content will be, so that you avoid the scrollbar jumping
   * all over the place as things load.
   *
   * If `removeWhenInvisible` is `true` and `customizePlaceholder` is a function, the given
   * function will be called with the value of clientHeight before it was removed from the DOM.
   * This allows a the placeholder to have the same height as the removed content and prevents
   * unnecessarily changes to the scroll height of the page.
   */
  customizePlaceholder?:
    | React.ReactElement
    | ((contentHeight: number | undefined) => React.ReactElement);

  /**
   * Specifies close the element needs to be to viewport before real children actually get
   * rendered. Choosing a large threshold will do a better job of getting things rendered
   * before they scroll into view, but it will also aggressively render things you might want
   * deferred longer. The default value is -100 which is suitable for things that are cheap
   * to load, or have fast networks available.
   *
   * Notice that if threshold is provided, the component will use it for both topThreshold
   * and bottomThreshold
   */
  threshold?: ResponsiveProp<number>;
  topThreshold?: ResponsiveProp<number>;
  bottomThreshold?: ResponsiveProp<number>;
  /**
   * Optional boolean to make the children rendered even if they are not close to the viewport
   * yet.
   *
   * @deprecated use the enableSsr prop or render the component directly instead
   */
  forceRender?: boolean;
  /**
   * When set to true, the children will be rendered on the server and during hydration.
   *
   * Note that since once content is rendered, it will not be unmounted due to
   * viewport changes, this prop effectively disables the
   * WhenVisibilityIsImminent optimization on content rendered using SSR.
   *
   * This prop only affects SSR: pages loaded using client side rendering, or
   * content added after the initial render are unaffected.
   *
   * Set this prop to true for content that either:
   *  - may be above the fold (especially if relevant to LCP) OR
   *  - is important for SEO purposes
   */
  enableSsr?: boolean;
  /**
   * Optional boolean to turn on interval check to detect visibility without a scroll event.
   */
  intervalCheck?: boolean;
  /**
   * Optional integer, number of milliseconds between checking the element's position in
   * relation the the window viewport.
   */
  intervalDelay?: number;
  /**
   * boolean to be used "ONLY" from the test class of this component.
   * This component will force render when testing unless the following flag is set to "true"
   */
  shouldRunNormallyInTest?: boolean;
  /**
   * When it is set to true, the children component will be removed from DOM when it is invisible.
   * This is useful to keep the number of nodes low in DOM. Please make sure to provide a placeholder
   * with a reasonable height.
   */
  removeWhenInvisible?: boolean;
  /**
   * use startTransition to set the visibility of the children component.
   */
  useTransition?: boolean;
}

interface IState {
  visible: boolean;
  placeholderHeight: number | undefined;
}

const WhenVisibilityIsImminentBase = (props: IProps) => {
  /*
   * TL;DR: we want to force render in test env.
   *
   * When testing a component that uses <WhenVisibilityIsImminent> in its render method, we want to
   * force this component to render. If we don't make this change here, then in each test we have to
   * make some changes to force this component to render.
   *
   * The only exception for a test class where we don't force rendering is "WhenVisibilityIsImminent.test.tsx"
   * since we want to test its functionality properly.
   * */
  const testEnv =
    process.env.NODE_ENV === "test" && !props.shouldRunNormallyInTest;
  const ssr = useIsSSR();
  const shouldForceRender =
    (testEnv ||
      getGlobalProperty("isPrerender", false) ||
      (ssr && props.enableSsr) ||
      isChromatic() ||
      props.forceRender) ??
    false;

  return (
    <IntersectionObserverImplementation
      {...props}
      shouldForceRender={shouldForceRender}
    />
  );
};

/**
 * This component allows content to be deferred from rendering when it is currently off the
 * visibile viewport by some threshold. Very useful to speed up rendering on a page's primary
 * content. E.g. on a page with 100 product-tiles (each tile having fairly deep component tree),
 * we get meaningful rendering savings by wrapping each tile in this component.
 *
 * Since this component is only intended to improve page load performance, once content has been
 * rendered, it will not be unmounted.
 *
 * <p>Child components of this can optionally be lazily loaded, which allows for the javascript
 * to also be deferred!
 *
 * <p>Example usage:
 * <pre>
 *
 *    <WhenVisibilityIsImminent customizePlaceholder={this.renderFallBack()}>
 *      <React.Suspense fallback={this.renderFallBack()}>
 *        <LazyExpensiveComponent />
 *      </React.Suspense>
 *   </WhenVisibilityIsImminent>
 *
 * </pre>
 */
export const WhenVisibilityIsImminent = memo(WhenVisibilityIsImminentBase);

interface IInternalProps extends IProps {
  shouldForceRender: boolean;
}

const isInIframe = () => {
  try {
    return !Object.is(getWindow()?.self, getWindow()?.top);
  } catch (_e) {
    return true;
  }
};

const supportDocumentRoot = () => {
  let document_root_supported = true;
  try {
    new IntersectionObserver(() => {}, {
      root: getWindow()!.document,
    });
  } catch (e) {
    if (e instanceof TypeError) {
      document_root_supported = false;
    }
  }
  return document_root_supported;
};

const IntersectionObserverImplementation: React.FC<IInternalProps> = ({
  shouldForceRender,
  removeWhenInvisible,
  threshold,
  topThreshold: responsiveTopThreshold = threshold ?? -100,
  bottomThreshold: responsiveBottomThreshold = threshold ?? -100,
  customizePlaceholder,
  children,
  useTransition,
}) => {
  const topThreshold = useResponsiveProp(responsiveTopThreshold);
  const bottomThreshold = useResponsiveProp(responsiveBottomThreshold);

  const [state, setState] = useState<IState>({
    visible: shouldForceRender,
    placeholderHeight: undefined,
  });

  useEffect(() => {
    setState((state) => ({
      visible: shouldForceRender || state.visible,
      placeholderHeight: undefined,
    }));
  }, [shouldForceRender]);

  const handleVisible = () => {
    setState((state) => {
      if (state.visible) {
        return state;
      }
      return {
        visible: true,
        placeholderHeight: undefined,
      };
    });
  };

  const handleInvisible = () => {
    setState((state) => {
      if (!state.visible) {
        return state;
      }
      let height = undefined;
      const clientHeight = childRef.current?.firstElementChild?.clientHeight;
      if (clientHeight && isFinite(clientHeight)) {
        height = Math.round(clientHeight);
      }
      return {
        visible: false,
        placeholderHeight: height ?? state.placeholderHeight,
      };
    });
  };

  const intersectionCallBack = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (
        entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)
      ) {
        if (useTransition) {
          startTransition(() => {
            handleVisible();
          });
        } else {
          handleVisible();
        }
        // disconnect the observer once the element is visible when removeWhenInvisible is false
        if (!removeWhenInvisible) {
          intersectionObserver.current?.disconnect();
        }
      } else if (removeWhenInvisible) {
        handleInvisible();
      }
    },
    [removeWhenInvisible, useTransition]
  );

  const initializeIntersectionObserver = useCallback(() => {
    const inIframe = isInIframe();
    let root: Document | Element | null | undefined = null;
    if (inIframe) {
      if (supportDocumentRoot()) {
        root = getDocument();
      } else {
        root = getDocument()?.scrollingElement;
      }
    }
    return new IntersectionObserver(intersectionCallBack, {
      // When we run e2e in cypress, the whole website is inside an iframe,
      // and that means by default the intersection observer will watch for
      // the cypress window's viewport, so in this case we need to specify
      // a root element. The best solution here is to pass document directly
      // as document.scrollingElement.getBoundingClientRect gives the full
      // size of page content instead of just the viewport (which means
      // targets will always intersect with the root and trigger the handleVisible
      // method). Since not all browsers (ie. Safari 13.1.2) support document
      // root, we need to do a check here.
      root,
      rootMargin: `${-topThreshold}px 0px ${-bottomThreshold}px 0px`,
    });
  }, [bottomThreshold, intersectionCallBack, topThreshold]);

  const intersectionObserver = useRef<IntersectionObserver | undefined>(
    undefined
  );

  const updateIntersectionObserver = useCallback(
    (node: HTMLDivElement | undefined) => {
      if (node && elementToObserveRef.current !== node.firstElementChild) {
        if (elementToObserveRef.current) {
          intersectionObserver.current?.unobserve(elementToObserveRef.current);
        }
        elementToObserveRef.current = node.firstElementChild;
        if (elementToObserveRef.current) {
          intersectionObserver.current?.observe(elementToObserveRef.current);
        }
      }
    },
    []
  );

  const childRef = useRef<HTMLDivElement | undefined>(undefined);
  const elementToObserveRef = useRef<Element | null>(null);
  const mutationObserverRef = useRef<MutationObserver | undefined>(undefined);

  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return;
      }

      // Disconnect the previous observers
      intersectionObserver.current?.disconnect();
      mutationObserverRef.current?.disconnect();

      // Initialize the intersection observer when the node is mounted
      intersectionObserver.current = initializeIntersectionObserver();

      // Set any previous observerd element to null
      elementToObserveRef.current = null;

      childRef.current = node;

      // We can't just watch `node`, because IntersectionObserver doesn't work with `display: contents`.
      // So, we watch the first child of `node` instead. We handle cases where the element can change.
      mutationObserverRef.current = new MutationObserver(() => {
        updateIntersectionObserver(node);
      });
      mutationObserverRef.current.observe(node, { childList: true });
      updateIntersectionObserver(node);
    },
    [updateIntersectionObserver, initializeIntersectionObserver]
  );

  useEffect(() => {
    return () => {
      intersectionObserver.current?.disconnect();
      mutationObserverRef.current?.disconnect();
    };
  }, []);

  const shouldDisplayPlaceHolder = !state.visible;
  if (!shouldForceRender && (shouldDisplayPlaceHolder || removeWhenInvisible)) {
    return (
      <Contents ref={refCallback}>
        {shouldDisplayPlaceHolder ? (
          <Placeholder
            customizePlaceholder={customizePlaceholder}
            height={state.placeholderHeight}
          />
        ) : (
          <>{children}</>
        )}
      </Contents>
    );
  } else {
    return children;
  }
};

const Placeholder: React.FC<
  Pick<IProps, "customizePlaceholder"> & {
    height?: number;
  }
> = ({ customizePlaceholder, height }) => {
  if (typeof customizePlaceholder === "function") {
    return customizePlaceholder(height);
  } else if (customizePlaceholder) {
    return customizePlaceholder;
  } else {
    return <div style={{ height: "10px" }} />;
  }
};

const Contents = styled.div`
  display: contents;
`;
